import { IconSearch, IconStack, IconStack2, IconStack3 } from '@tabler/icons-react'
import { type ReactNode, useId, useState } from 'react'
import Select, { components, type SingleValue } from 'react-select'

import { AstraCallout, AstraGlassCard, AstraIcon, AstraParagraph, astraTwMerge } from '~/astra'
import { SurgeryTypeGroupEnum } from '~/clients/derived-api-types'
import { type HospitalSurgeryType, type ResolvedSurgeryTypeGroup, useGetResolvedSurgeryTypeGroup } from '~/storm'

const MENU_HEIGHT = 400

const groupLevelLabel: Record<SurgeryTypeGroupEnum, string> = {
    speciality: 'Spesialiseringer',
    subspeciality: 'Kategorigrupper',
    composite: 'Kategorigrupper',
    category: 'Kategorier',
}

const groupLevelOrder: Record<SurgeryTypeGroupEnum, number> = {
    speciality: 1,
    subspeciality: 2,
    composite: 3,
    category: 4,
}

const groupLevelIcon: Record<SurgeryTypeGroupEnum, ReactNode> = {
    speciality: <AstraIcon icon={IconStack3} />,
    subspeciality: <AstraIcon icon={IconStack2} />,
    composite: <AstraIcon icon={IconStack2} />,
    category: <AstraIcon icon={IconStack} />,
}

type SurgeryTypeGroupOption = {
    label: string
    value: string
    type: SurgeryTypeGroupEnum
    isDisabled: boolean
}

function isSurgeryTypeGroupOption(value: unknown): value is SurgeryTypeGroupOption {
    return typeof value === 'object' && value !== null && 'label' in value && 'value' in value && 'type' in value && 'isDisabled' in value
}

type SurgeryTypeGroupOptionGroup = {
    label: string
    options: SurgeryTypeGroupOption[]
}

function HighlightedText({ text, search }: { text: string; search: string }) {
    if (!search) {
        return text
    }

    const searchMatchIndex = text.toLocaleLowerCase().indexOf(search.toLocaleLowerCase())

    if (searchMatchIndex === -1) {
        return text
    }

    const prefix = text.slice(0, searchMatchIndex)
    const match = text.slice(searchMatchIndex, searchMatchIndex + search.length)
    const suffix = text.slice(searchMatchIndex + search.length)

    return (
        <span>
            {prefix}
            <span className="bg-yellow-200">{match}</span>
            {suffix}
        </span>
    )
}

function getGroupedOptions(surgeryTypeGroups: ResolvedSurgeryTypeGroup[], disabledOptions: readonly string[]) {
    const disabledOptionCodes = new Set(disabledOptions)

    return surgeryTypeGroups
        .filter(surgeryTypeGroup => !surgeryTypeGroup.hidden)
        .toSorted((a, b) => {
            const groupOrder = groupLevelOrder[a.group_type] - groupLevelOrder[b.group_type]

            if (groupOrder !== 0) {
                return groupOrder
            }

            return a.display_name.localeCompare(b.display_name)
        })
        .reduce<SurgeryTypeGroupOptionGroup[]>((groups, surgeryTypeGroup) => {
            const groupLabel = groupLevelLabel[surgeryTypeGroup.group_type]
            const existingGroupIndex = groups.findIndex(group => group.label === groupLabel)
            const option = {
                label: surgeryTypeGroup.display_name,
                value: surgeryTypeGroup.code,
                type: surgeryTypeGroup.group_type,
                isDisabled: disabledOptionCodes.has(surgeryTypeGroup.code),
            }

            if (existingGroupIndex === -1) {
                return [...groups, { label: groupLabel, options: [option] }]
            }

            return groups.map((group, index) => {
                if (index !== existingGroupIndex) {
                    return group
                }

                return {
                    ...group,
                    options: [...group.options, option].toSorted((a, b) => a.label.localeCompare(b.label)),
                }
            })
        }, [])
}

function filterSurgeryTypeGroupOption(option: { data: SurgeryTypeGroupOption }, inputValue: string) {
    return option.data.label.toLocaleLowerCase().includes(inputValue.toLocaleLowerCase())
}

const SurgeryTypeList = ({ surgeryTypes }: { surgeryTypes: HospitalSurgeryType[] }) => (
    <div className="flex h-[300px] flex-col">
        <AstraParagraph variant="body-small" weight="medium" className="px-2 py-3 text-subtle">
            DIPS operasjonstyper
        </AstraParagraph>
        <ul className="user-select flex flex-1 list-disc flex-col gap-2 overflow-y-auto px-4" style={{ maxHeight: MENU_HEIGHT - 50 }}>
            {surgeryTypes.map(surgeryType => (
                <li key={surgeryType.uuid} className="ml-4 whitespace-nowrap">
                    <AstraParagraph className="truncate">{surgeryType.name}</AstraParagraph>
                </li>
            ))}
        </ul>
        {!surgeryTypes.length && (
            <div className="p-2">
                <AstraCallout variant="outlined" text="Velg operasjonskategorier eller operasjonskategorigrupper for å se DIPS-operasjonstyper." />
            </div>
        )}
    </div>
)

type Props = {
    value: string | null
    onChange: (code: string | null) => void
    disabledOptions?: readonly string[]
    placeholder?: string
    autoFocus?: boolean
    'data-test'?: string
    'aria-label'?: string
}

export function SurgeryTypeGroupComboBox({
    disabledOptions = [],
    placeholder = 'Finn operasjonskategori eller gruppe',
    autoFocus = true,
    value,
    onChange,
    'aria-label': ariaLabel = 'Søk etter operasjonskategori eller gruppe',
    'data-test': dataTest,
}: Props) {
    const inputId = useId()
    const surgeryTypeGroups = useGetResolvedSurgeryTypeGroup()
    const options = getGroupedOptions(surgeryTypeGroups.all, disabledOptions)

    // Lazy init avoids re-running the byCode lookup on every render.
    const [inputValue, setInputValue] = useState(() => surgeryTypeGroups.byCode(value ?? '')?.display_name ?? '')

    function handleChange(option: SingleValue<SurgeryTypeGroupOption>) {
        onChange(option?.value ?? null)
        setInputValue('')
    }

    return (
        <AstraGlassCard data-test={dataTest}>
            <Select
                aria-label={ariaLabel}
                autoFocus={autoFocus}
                blurInputOnSelect={false}
                classNames={{
                    container: () => 'w-[700px] overflow-hidden rounded-lg',
                    control: () => 'flex min-h-14 items-center gap-3 border-b border-base bg-surface-base px-5 py-4',
                    group: () => 'relative border-none',
                    groupHeading: () => 'p-2',
                    input: () =>
                        'm-0 p-0 focus:outline-none active:outline-none disabled:cursor-not-allowed disabled:border-disabled disabled:text-disabled placeholder:text-subtle',
                    menu: () => 'z-priority w-[700px] overflow-hidden bg-surface-base',
                    noOptionsMessage: () => 'flex gap-2',
                    option: state =>
                        astraTwMerge(
                            'mx-0 !flex min-h-7 cursor-pointer items-center gap-2 overflow-hidden rounded-md px-2 py-1',
                            state.isSelected ? 'bg-neutral-subtle-active' : '',
                            state.isFocused && !state.isSelected ? 'bg-neutral-subtle-hover' : '',
                            !state.isSelected ? 'hover:bg-neutral-subtle-hover' : '',
                            state.isDisabled ? 'cursor-not-allowed text-disabled' : 'text-default'
                        ),
                    placeholder: () => 'm-0 text-subtle',
                    singleValue: () => 'm-0 text-default',
                    valueContainer: () => 'flex min-w-0 flex-1 flex-row items-center gap-2 overflow-hidden whitespace-nowrap p-0',
                }}
                closeMenuOnSelect={false}
                components={{
                    Control: props => (
                        <components.Control {...props}>
                            <AstraIcon icon={IconSearch} color="text-subtle" />
                            {props.children}
                        </components.Control>
                    ),
                    DropdownIndicator: () => null,
                    GroupHeading: props => (
                        <components.GroupHeading {...props}>
                            <AstraParagraph variant="body-small" weight="medium" className="text-subtle">
                                {props.children}
                            </AstraParagraph>
                        </components.GroupHeading>
                    ),
                    IndicatorSeparator: () => null,
                    Input: props => <components.Input {...props} autoFocus={props.selectProps.autoFocus} />,
                    MenuList: ({ focusedOption, ...props }) => {
                        const surgeryTypes = isSurgeryTypeGroupOption(focusedOption) ? (surgeryTypeGroups.surgeryTypesByCode(focusedOption.value) ?? []) : []

                        return (
                            <div className="flex h-[300px] bg-surface-base" tabIndex={-1}>
                                <div ref={props.innerRef} {...props.innerProps} className="h-[300px] w-1/2 overflow-y-auto px-2 py-3" tabIndex={-1}>
                                    {props.children}
                                </div>
                                <div
                                    className="w-1/2 bg-surface-base pl-1 pr-5"
                                    onClick={event => {
                                        event.stopPropagation()
                                        event.preventDefault()
                                    }}
                                >
                                    <SurgeryTypeList surgeryTypes={surgeryTypes} />
                                </div>
                            </div>
                        )
                    },
                    NoOptionsMessage: props => (
                        <components.NoOptionsMessage {...props}>
                            <div className="w-full">
                                <AstraCallout text="Ingen resultater" />
                            </div>
                        </components.NoOptionsMessage>
                    ),
                    Option: props => (
                        <components.Option {...props}>
                            {groupLevelIcon[props.data.type]}
                            <AstraParagraph variant="body" weight="regular" className="min-w-0 truncate whitespace-nowrap text-inherit">
                                <HighlightedText text={props.data.label} search={props.selectProps.inputValue} />
                            </AstraParagraph>
                        </components.Option>
                    ),
                }}
                filterOption={filterSurgeryTypeGroupOption}
                inputId={inputId}
                inputValue={inputValue}
                instanceId={inputId}
                isOptionDisabled={option => option.isDisabled}
                menuIsOpen={true}
                menuPlacement="bottom"
                onChange={handleChange}
                onInputChange={(nextInputValue, actionMeta) => {
                    if (actionMeta.action === 'input-change') {
                        setInputValue(nextInputValue)
                    }
                }}
                openMenuOnFocus={true}
                options={options}
                placeholder={placeholder}
                styles={{
                    menu: base => ({
                        ...base,
                        position: 'static',
                        width: '100%',
                        marginBottom: 0,
                        marginTop: 0,
                    }),
                }}
                unstyled={true}
            />
        </AstraGlassCard>
    )
}
